/**
 * SalesQueue - Gestion des ventes en mode hors-ligne
 *
 * Toutes les ventes passent par cette queue, qu'on soit online ou offline.
 * - En online : la vente est immédiatement synchronisée
 * - En offline : la vente est stockée dans IndexedDB et synchronisée à la reconnexion
 */
import { v4 as uuidv4 } from 'uuid';
import { offlineDb, PendingSale, PendingSaleLine, LocalStockMovement } from './OfflineDatabase';
import { CatalogSync } from './CatalogSync';
import { api } from '../../services/api';
import { logger } from '../../utils/logger';
import { buildSaleLines, type CartLine as PosCartLine } from '../../utils/pos.utils';

// ============================================================================
// TYPES
// ============================================================================

export interface CartLine {
  product: {
    id: string;
    name: string;
    tax_class?: string;
  };
  product_name?: string;
  note?: string;
  qty: number;
  unit_price_cents: number;
  tax_rate?: number;
  is_deposit?: boolean;
  is_return?: boolean;
  is_custom?: boolean;
  is_gift_voucher?: boolean;
  is_refund?: boolean;
  lot_id?: string;
  lot_promo_percent?: number;
  original_price_cents?: number;
}

export interface CreateSaleParams {
  cartLines: CartLine[];
  vendorId: string | null;
  vendorName: string | null;
  customerId: string | null;
  preorderId: string | null;
  paymentMethod: string;
  amountPaidCents: number | null;
  paymentMetadata?: Record<string, number> | null;
  ecomOrderId?: string | null;
  salesChannel?: string;
  invoiceRequested?: boolean;
}

export interface SyncResult {
  synced: number;
  failed: number;
  conflicts: number;
  pending: number;
}

// Timeout du POST de synchro d'une vente. SANS ÇA, une requête qui se fige (réseau à
// moitié ouvert, serveur lent) pend à l'infini → la vente reste en 'syncing', invisible,
// jamais renvoyée → vente encaissée PERDUE (incident marché 28/06/2026). Avec timeout,
// une requête figée ÉCHOUE → la vente passe en 'error' VISIBLE et est ré-essayée.
const SALE_SYNC_TIMEOUT_MS = 20_000;
// Une vente 'syncing' depuis plus longtemps que ça est considérée figée (le timeout
// aurait dû la libérer) → on la requalifie en 'pending' même si elle est encore dans le
// verrou mémoire. Un éventuel double envoi est neutralisé par l'idempotence serveur
// (offline_sale_id). Ceinture + bretelles par-dessus le timeout.
const SYNCING_STALE_MS = 45_000;

// ============================================================================
// CLASSE PRINCIPALE
// ============================================================================

export class SalesQueue {
  private tenantId: string;
  private syncInProgress = false;
  private catalogSync: CatalogSync;
  // Ventes en cours d'envoi (évite que la sync immédiate ET la sync périodique
  // postent la MÊME vente en parallèle → doublon malgré l'idempotence serveur).
  private syncingIds = new Set<string>();
  /**
   * Dernière preuve SERVEUR de l'état du réseau — surtout pas le drapeau du
   * navigateur, que la file consultait jusqu'ici à cinq endroits.
   *
   * Ce drapeau n'est fiable dans aucun des deux sens, et Safari/macOS le laisse
   * parfois bloqué à « faux » après un réveil Wi-Fi sans jamais le remettre à
   * « vrai » (POS-03, 06/09/2026). Depuis que la sonde HEAD prouve le retour du
   * réseau sans lui (correctif du 08/09), la caisse pouvait donc afficher un
   * badge VERT pendant que la file, elle, restait verrouillée sur le drapeau
   * bloqué : aucune vente espèces ou Bancontact n'était même tentée, le bouton
   * « Synchroniser » tournait sans rien faire, `retrySale` et `forceFullSale`
   * renvoyaient un succès sans rien envoyer, et la clôture Z restait refusée
   * sans que l'écran puisse dire pourquoi. Rien n'était perdu, mais la journée
   * ne se fermait plus.
   *
   * Repli OUVERT (« vrai » par défaut) : dans le doute on tente, et l'échec de
   * la requête fait foi — c'est la seule preuve d'être hors ligne qui vaille.
   * Ne pas tenter est justement ce qui bloque une journée entière.
   */
  private serverReachable = true;

  constructor(tenantId: string) {
    this.tenantId = tenantId;
    this.catalogSync = new CatalogSync(tenantId);
  }

  /**
   * Renseigner la file sur l'état du réseau PROUVÉ par le serveur.
   *
   * Appelé par `useOfflineMode`, seul détenteur de cette preuve (sonde HEAD).
   * Sert uniquement à économiser des requêtes au marché sans réseau : la file
   * ne s'en sert JAMAIS pour se déclarer durablement hors ligne toute seule.
   */
  setServerReachable(reachable: boolean): void {
    this.serverReachable = reachable;
  }

  /**
   * Créer une vente (toujours locale d'abord, puis sync si online)
   */
  async createSale(params: CreateSaleParams): Promise<PendingSale> {
    const id = uuidv4();
    const localSaleNumber = this.generateLocalSaleNumber();

    // Calculer les totaux
    const { totalCents, taxTotalCents, lines } = this.calculateTotals(params.cartLines);

    // Calculer le rendu monnaie (espèces pures ou partie espèces d'un mixte)
    const cashAmount = params.paymentMethod === 'cash'
      ? params.amountPaidCents
      : params.paymentMetadata?.cash_cents || null;
    const changeCents = cashAmount && cashAmount > 0
      ? Math.max(0, (params.amountPaidCents || cashAmount) - totalCents)
      : null;

    // Créer la vente locale
    const pendingSale: PendingSale = {
      id,
      localSaleNumber,
      tenant_id: this.tenantId,
      vendor_id: params.vendorId,
      vendor_name: params.vendorName,
      customer_id: params.customerId,
      preorder_id: params.preorderId,
      ecom_order_id: params.ecomOrderId || null,
      sales_channel: params.salesChannel || 'pos',
      invoice_requested: params.invoiceRequested || false,
      payment_method: params.paymentMethod,
      amount_paid_cents: params.amountPaidCents,
      payment_metadata: params.paymentMetadata || null,
      total_cents: totalCents,
      tax_total_cents: taxTotalCents,
      change_cents: changeCents,
      lines,
      status: 'pending',
      retryCount: 0,
      created_at: Date.now(),
    };

    // Sauvegarder dans IndexedDB
    await offlineDb.pendingSales.add(pendingSale);
    logger.log(`[SalesQueue] 💾 Vente ${localSaleNumber} créée localement`);

    // Enregistrer les mouvements de stock locaux
    await this.recordLocalStockMovements(id, lines);

    // Mettre à jour le stock local des produits
    await this.updateLocalProductStock(lines);

    // Tenter la sync immédiate si le serveur a répondu récemment.
    if (this.serverReachable) {
      // Sync en arrière-plan, ne pas bloquer
      this.syncSingleSale(pendingSale).catch(err => {
        logger.warn(`[SalesQueue] Sync immédiate échouée, sera réessayée:`, err);
      });
    }

    return pendingSale;
  }

  /**
   * Générer un numéro de vente local unique
   */
  private generateLocalSaleNumber(): string {
    const now = new Date();
    const date = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    // 9 chiffres (audit ISCA 30/08/2026) : à 5, deux caisses tiraient le même numéro
    // dès ~95 ventes/jour et le serveur renumérotait en silence (ticket ≠ base).
    const random = String(Math.floor(100_000_000 + Math.random() * 900_000_000));
    return `SALE-${date}-${random}`;
  }

  /**
   * Calculer les totaux à partir du panier
   * Utilise buildSaleLines de pos.utils comme source unique de vérité pour le tax_rate
   */
  private calculateTotals(cartLines: CartLine[]): {
    totalCents: number;
    taxTotalCents: number;
    lines: PendingSaleLine[];
  } {
    // Convertir vers le format CartLine de pos.utils et utiliser buildSaleLines
    const posCartLines: PosCartLine[] = cartLines.map((line) => ({
      product: line.product?.id ? {
        id: line.product.id,
        name: line.product.name,
        tax_class: line.product.tax_class,
      } : null,
      product_name: line.product_name,
      note: line.note,
      qty: line.qty,
      unit_price_cents: line.unit_price_cents,
      tax_rate: line.tax_rate,
      is_deposit: line.is_deposit,
      is_return: line.is_return,
      is_custom: line.is_custom,
      is_gift_voucher: line.is_gift_voucher,
      is_refund: line.is_refund,
      lot_id: line.lot_id,
      lot_promo_percent: line.lot_promo_percent,
      original_price_cents: line.original_price_cents,
    }));

    // buildSaleLines calcule le tax_rate correctement (source unique de vérité)
    const saleLines = buildSaleLines(posCartLines);

    // Enrichir avec total_price_cents et calculer les totaux
    let totalCents = 0;
    let taxTotalCents = 0;
    const lines: PendingSaleLine[] = [];

    for (const saleLine of saleLines) {
      // Arrondi au cent PAR LIGNE : c'est cet objet local qui alimente le
      // ticket imprimé (POSContent → receiptGenerator). Sans arrondi, le total
      // du ticket divergeait de celui de l'écran et de celui enregistré au
      // serveur — dès qu'un produit pesé ne tombait pas sur un centime rond.
      const lineTotalCents = Math.round(saleLine.unit_price_cents * Math.abs(saleLine.quantity));
      const taxCents = Math.round(lineTotalCents * saleLine.tax_rate / (1 + saleLine.tax_rate));

      // Pour les retours, le total est négatif
      const signedTotal = saleLine.is_return ? -lineTotalCents : lineTotalCents;
      const signedTax = saleLine.is_return ? -taxCents : taxCents;

      totalCents += signedTotal;
      taxTotalCents += signedTax;

      lines.push({
        ...saleLine,
        quantity: saleLine.is_return ? -Math.abs(saleLine.quantity) : saleLine.quantity,
        total_price_cents: signedTotal,
      });
    }

    return { totalCents, taxTotalCents, lines };
  }

  /**
   * Enregistrer les mouvements de stock locaux
   */
  private async recordLocalStockMovements(saleId: string, lines: PendingSaleLine[]): Promise<void> {
    const movements: LocalStockMovement[] = lines
      .filter(line => !line.is_return && line.quantity > 0 && line.product_id)
      .map(line => ({
        id: uuidv4(),
        product_id: line.product_id!,
        quantity_delta: -line.quantity, // Négatif car c'est une sortie
        reason: 'sale' as const,
        reference_id: saleId,
        created_at: Date.now(),
        synced: false,
      }));

    if (movements.length > 0) {
      await offlineDb.stockMovements.bulkAdd(movements);
    }
  }

  /**
   * Mettre à jour le stock local des produits
   */
  private async updateLocalProductStock(lines: PendingSaleLine[]): Promise<void> {
    for (const line of lines) {
      if (!line.is_return && line.quantity > 0 && line.product_id) {
        await this.catalogSync.updateLocalStock(line.product_id, line.quantity);
      }
    }
  }

  /**
   * Synchroniser une seule vente
   */
  private async syncSingleSale(sale: PendingSale): Promise<boolean> {
    // Verrou anti-concurrence : ne jamais envoyer 2× la même vente simultanément
    if (this.syncingIds.has(sale.id)) {
      logger.warn(`[SalesQueue] ⏳ Sync déjà en cours pour ${sale.localSaleNumber}, ignorée`);
      return false;
    }
    this.syncingIds.add(sale.id);
    try {
      // Marquer comme en cours de sync (+ heure → détection des requêtes figées).
      // Une vente en CONFLIT ou en ERREUR qu'on relance garde son état pendant
      // l'envoi : la relance a lieu toutes les 30 s, et la faire passer par
      // 'syncing' ferait retomber puis remonter le compteur de conflits à chaque
      // essai — clignotement de l'indicateur et alerte sonore « nouveau conflit »
      // répétée. Le verrou `syncingIds` suffit contre le double envoi, et le
      // serveur reconnaît une vente déjà reçue à son `offline_sale_id`.
      const relanceSilencieuse = sale.status === 'conflict' || sale.status === 'error';
      if (!relanceSilencieuse) {
        await offlineDb.pendingSales.update(sale.id, { status: 'syncing', syncing_at: Date.now() });
      }

      // Préparer les données pour l'API
      const saleDto = {
        tenant_id: sale.tenant_id,
        vendor_id: sale.vendor_id || undefined,
        customer_id: sale.customer_id || undefined,
        payment_method: sale.payment_method,
        amount_paid_cents: sale.amount_paid_cents || undefined,
        payment_metadata: sale.payment_metadata || undefined,
        ecom_order_id: sale.ecom_order_id || undefined,
        // Sans ce champ, une précommande remise HORS LIGNE n'était jamais fermée :
        // le POS ne la marquait « récupérée » qu'en ligne, et la synchronisation ne
        // transportait pas l'information. Elle restait PENDING indéfiniment.
        preorder_id: sale.preorder_id || undefined,
        sales_channel: sale.sales_channel || 'pos',
        invoice_requested: sale.invoice_requested || false,
        lines: sale.lines.map(line => ({
          product_id: line.product_id,
          product_name: line.product_name,
          quantity: line.quantity,
          unit_price_cents: Math.abs(line.unit_price_cents),
          tax_rate: line.tax_rate,
          is_deposit: line.is_deposit,
          is_return: line.is_return,
          // Cette énumération est la SEULE porte vers le serveur pour tout paiement
          // autre que wallet : un champ absent ici est un champ perdu, sans erreur
          // ni trace. C'est ainsi que `is_custom` et `notes` ne sont jamais arrivés
          // en base depuis l'ouverture.
          is_custom: line.is_custom,
          notes: line.notes,
          is_gift_voucher: line.is_gift_voucher,
          // Remise anti-gaspi par lot : même règle que ci-dessus, un champ
          // absent de cette énumération n'atteint jamais le serveur.
          lot_id: line.lot_id,
          lot_promo_percent: line.lot_promo_percent,
          original_price_cents: line.original_price_cents,
        })),
        // Métadonnées offline pour traçabilité multi-POS
        offline_sale_id: sale.id,
        offline_created_at: new Date(sale.created_at).toISOString(),
        offline_sale_number: sale.localSaleNumber,
        pos_id: localStorage.getItem('pos_id') || 'unknown',
        // Forçage oversell : le serveur enregistre la vente même si le stock est insuffisant
        force: sale.force === true ? true : undefined,
      };

      // Envoyer au serveur — timeout : une requête figée échoue (→ 'error' visible + retry)
      // au lieu de pendre à l'infini et laisser la vente invisible en 'syncing'.
      const response = await api.post('/pos/sales', saleDto, { timeout: SALE_SYNC_TIMEOUT_MS });

      // Marquer comme synchronisé
      await offlineDb.pendingSales.update(sale.id, {
        status: 'synced',
        serverSaleNumber: response.sale?.sale_number || response.sale_number,
        synced_at: Date.now(),
      });

      // Marquer les mouvements de stock comme synchronisés
      await offlineDb.stockMovements
        .where('reference_id')
        .equals(sale.id)
        .modify({ synced: true });

      logger.log(`[SalesQueue] ✅ Vente ${sale.localSaleNumber} synchronisée → ${response.sale?.sale_number}`);

      return true;

    } catch (error: any) {
      logger.error(`[SalesQueue] ❌ Erreur sync vente ${sale.localSaleNumber}:`, error);

      // Analyser l'erreur
      const data = error.response?.data;
      const errorMessage = data?.message || error.message || 'Erreur inconnue';
      const isBackroom = data?.error_type === 'BACKROOM_ONLY';
      // Détection robuste via le code structuré du serveur (error_type='INSUFFICIENT_STOCK',
      // BadRequest 400) plutôt que par texte. On garde un repli texte INSENSIBLE À LA CASSE :
      // le serveur renvoie « stock insuffisant » (minuscule), l'ancien test 'Stock insuffisant'
      // (majuscule) ne matchait jamais → vente oversold classée en erreur générique (retry 3×)
      // au lieu d'un conflit (forcer/annuler avec quantité dispo).
      const msgLower = String(errorMessage).toLowerCase();
      const isConflict = !isBackroom && (
        data?.error_type === 'INSUFFICIENT_STOCK' ||
        error.response?.status === 409 ||
        msgLower.includes('stock insuffisant') ||
        msgLower.includes('quantité insuffisante') ||
        msgLower.includes('quantite insuffisante') ||
        msgLower.includes('conflit')
      );

      // Extraire les détails du conflit si disponibles
      let conflictDetails = undefined;
      if (isConflict && data) {
        if (data.product_id) {
          conflictDetails = {
            product_id: data.product_id,
            product_name: data.product_name || 'Produit inconnu',
            requested: data.requested || 0,
            available: data.available || 0,
            own_production: data.own_production === true,
            checked_at: Date.now(),
          };
        }
      }

      // Extraire les détails backroom si disponibles
      let backroomDetails = undefined;
      if (isBackroom && data) {
        backroomDetails = {
          product_id: data.product_id,
          product_name: data.product_name || 'Produit inconnu',
          backroom_quantity: data.backroom_quantity || 0,
          lot_id: data.lot_id || null,
          requested: data.requested || 0,
        };
      }

      await offlineDb.pendingSales.update(sale.id, {
        status: isConflict ? 'conflict' : 'error',
        errorMessage,
        conflictDetails,
        backroomDetails,
        retryCount: sale.retryCount + 1,
      });

      return false;
    } finally {
      // Toujours libérer le verrou "en vol", succès comme échec
      this.syncingIds.delete(sale.id);
    }
  }

  /**
   * RECOVERY — une vente peut rester bloquée en 'syncing' à jamais si Safari est
   * fermé / le POS crashe pendant l'envoi (la promesse de sync ne se termine jamais).
   * Elle devient alors invisible de tous les compteurs ('syncing' n'est ni 'pending'
   * ni 'error') et n'est JAMAIS resynchronisée → vente encaissée perdue.
   * On requalifie en 'pending' toute vente 'syncing' qui n'est PAS réellement en vol
   * (this.syncingIds) → elle repart en sync. Au démarrage syncingIds est vide, donc
   * toutes les 'syncing' orphelines d'une session précédente crashée sont récupérées.
   */
  async recoverStaleSyncing(): Promise<number> {
    const stuck = await offlineDb.pendingSales
      .where('[tenant_id+status]')
      .equals([this.tenantId, 'syncing'])
      .toArray();
    const now = Date.now();
    let recovered = 0;
    for (const sale of stuck) {
      const inFlight = this.syncingIds.has(sale.id);
      const tooOld = sale.syncing_at != null && now - sale.syncing_at > SYNCING_STALE_MS;
      // On laisse les requêtes réellement en vol… SAUF si elles traînent trop longtemps
      // (figées : le timeout aurait dû les libérer) → on débloque le verrou et on requalifie.
      if (inFlight && !tooOld) continue;
      if (inFlight) this.syncingIds.delete(sale.id);
      await offlineDb.pendingSales.update(sale.id, { status: 'pending', errorMessage: undefined });
      recovered++;
    }
    if (recovered > 0) {
      logger.warn(`[SalesQueue] ♻️ ${recovered} vente(s) bloquée(s) en 'syncing' récupérée(s) → pending`);
    }
    return recovered;
  }

  /**
   * Synchroniser toutes les ventes en attente
   */
  async syncPendingSales(): Promise<SyncResult> {
    if (this.syncInProgress || !this.serverReachable) {
      return { synced: 0, failed: 0, conflicts: 0, pending: await this.getPendingCount() };
    }

    this.syncInProgress = true;
    const result: SyncResult = { synced: 0, failed: 0, conflicts: 0, pending: 0 };

    try {
      // Récupérer d'abord les ventes orphelines bloquées en 'syncing' (crash/fermeture)
      await this.recoverStaleSyncing();

      // Récupérer les ventes à synchroniser
      const pendingSales = await offlineDb.pendingSales
        .where('[tenant_id+status]')
        .equals([this.tenantId, 'pending'])
        .toArray();

      // Inclure aussi les erreurs (sans plafond de tentatives) ET les conflits de
      // stock. Un conflit n'était jamais relancé : la vente restait bloquée, figée
      // sur les chiffres du refus, jusqu'à ce que quelqu'un la « force » — alors
      // qu'une fois la récolte ou la réception encodée elle passe normalement
      // (constat du 26/09/2026 : 8 cas en trois semaines, tous de la production
      // maison vendue avant d'être encodée). Relancée SANS forcer : si le stock
      // manque toujours, elle reste en conflit avec des chiffres à jour.
      const errorSales = await offlineDb.pendingSales
        .where('tenant_id')
        .equals(this.tenantId)
        .and(sale => sale.status === 'error' || sale.status === 'conflict')
        .toArray();

      const allToSync = [...pendingSales, ...errorSales];
      result.pending = allToSync.length;

      logger.log(`[SalesQueue] 🔄 Synchronisation de ${allToSync.length} vente(s)...`);

      // Les abandons non encore enregistrés côté serveur partent d'abord

      await this.syncAbandonedSales();


      // Synchroniser dans l'ordre chronologique
      allToSync.sort((a, b) => a.created_at - b.created_at);

      for (const sale of allToSync) {
        const success = await this.syncSingleSale(sale);

        if (success) {
          result.synced++;
        } else {
          // Vérifier si c'est un conflit ou une erreur
          const updatedSale = await offlineDb.pendingSales.get(sale.id);
          if (updatedSale?.status === 'conflict') {
            result.conflicts++;
          } else {
            result.failed++;
          }
        }
      }

      // Log de sync
      if (result.synced > 0 || result.failed > 0 || result.conflicts > 0) {
        await offlineDb.syncLogs.add({
          timestamp: Date.now(),
          type: 'sales',
          status: result.failed === 0 && result.conflicts === 0 ? 'success' : 'partial',
          details: JSON.stringify(result),
          itemCount: result.synced,
        });
      }

      logger.log(`[SalesQueue] ✅ Sync terminée: ${result.synced} ok, ${result.conflicts} conflits, ${result.failed} erreurs`);

    } finally {
      this.syncInProgress = false;
    }

    return result;
  }

  /**
   * Obtenir le nombre de ventes en attente
   */
  async getPendingCount(): Promise<number> {
    return offlineDb.pendingSales
      .where('[tenant_id+status]')
      .equals([this.tenantId, 'pending'])
      .count();
  }

  /**
   * Obtenir les ventes en conflit
   */
  async getConflictSales(): Promise<PendingSale[]> {
    return offlineDb.pendingSales
      .where('tenant_id')
      .equals(this.tenantId)
      .and(sale => sale.status === 'conflict')
      .toArray();
  }

  /**
   * Obtenir les ventes en erreur
   */
  async getErrorSales(): Promise<PendingSale[]> {
    return offlineDb.pendingSales
      .where('tenant_id')
      .equals(this.tenantId)
      .and(sale => sale.status === 'error')
      .toArray();
  }

  /**
   * Obtenir toutes les ventes non synchronisées
   */
  async getUnsyncedSales(): Promise<PendingSale[]> {
    return offlineDb.pendingSales
      .where('tenant_id')
      .equals(this.tenantId)
      .and(sale => sale.status !== 'synced')
      .toArray();
  }

  /**
   * Réessayer une vente en erreur
   */
  async retrySale(saleId: string): Promise<boolean> {
    const sale = await offlineDb.pendingSales.get(saleId);
    if (!sale) return false;

    // Remettre en pending
    await offlineDb.pendingSales.update(saleId, {
      status: 'pending',
      errorMessage: undefined,
      conflictDetails: undefined,
    });

    // Tenter la sync
    if (this.serverReachable) {
      return this.syncSingleSale(sale);
    }

    return true;
  }

  /**
   * Annuler une vente non synchronisée
   * (restaure le stock local et supprime la vente)
   */
  /**
   * ABANDONNER une vente hors-ligne (conflit insoluble) — remplace l'ancienne
   * suppression pure d'IndexedDB (incident 26/06/2026 : 68,21 € Bancontact débités,
   * vente effacée sans trace). La vente est marquée, son contenu et le motif sont
   * envoyés au serveur ; elle bloque la clôture tant que cet envoi n'a pas abouti.
   */
  async abandonPendingSale(saleId: string, reason: string): Promise<boolean> {
    const sale = await offlineDb.pendingSales.get(saleId);
    if (!sale || sale.status === 'synced' || sale.status === 'syncing') {
      return false;
    }
    const motif = (reason || '').trim();
    if (!motif) return false;
    // Restaurer le stock local (la marchandise n'est pas partie… ou l'inventaire tranchera)
    for (const line of sale.lines) {
      if (!line.is_return && line.quantity > 0 && line.product_id) {
        await offlineDb.products
          .where('id')
          .equals(line.product_id)
          .modify(product => {
            product.stock_quantity += line.quantity;
            product.stock_display += line.quantity;
          });
      }
    }
    await offlineDb.stockMovements.where('reference_id').equals(saleId).delete();
    await offlineDb.pendingSales.update(saleId, {
      status: 'abandoned',
      abandonReason: motif,
      abandoned_at: Date.now(),
      abandon_synced: false,
    });
    logger.warn(`[SalesQueue] ⚠️ Vente ${sale.localSaleNumber} ABANDONNÉE : ${motif}`);
    await this.syncAbandonedSales();
    return true;
  }

  /** Envoie au serveur les abandons pas encore enregistrés (idempotent côté serveur). */
  async syncAbandonedSales(): Promise<number> {
    if (!this.serverReachable) return 0;
    const toSend = await offlineDb.pendingSales
      .where('tenant_id')
      .equals(this.tenantId)
      .and(s => s.status === 'abandoned' && !s.abandon_synced)
      .toArray();
    let sent = 0;
    for (const sale of toSend) {
      try {
        await api.post(
          '/pos/sales/abandoned',
          {
            offline_sale_id: sale.id,
            local_sale_number: sale.localSaleNumber,
            pos_id: localStorage.getItem('pos_id') || undefined,
            vendor_id: sale.vendor_id || undefined,
            reason: sale.abandonReason || 'abandon',
            total_cents: sale.total_cents,
            payment_method: sale.payment_method,
            offline_created_at: new Date(sale.created_at).toISOString(),
            payload: { lines: sale.lines, payment_metadata: sale.payment_metadata ?? null, customer_id: sale.customer_id ?? null, abandoned_at: sale.abandoned_at ?? null },
          },
          { timeout: SALE_SYNC_TIMEOUT_MS },
        );
        await offlineDb.pendingSales.update(sale.id, { abandon_synced: true });
        sent++;
      } catch (error) {
        logger.error(`[SalesQueue] ❌ Abandon de ${sale.localSaleNumber} non enregistré côté serveur (nouvel essai à la prochaine sync)`, error);
      }
    }
    return sent;
  }

  /**
   * Forcer l'enregistrement de la vente COMPLÈTE malgré un stock insuffisant.
   * Le produit a été physiquement vendu et encaissé hors-ligne : la vente DOIT être
   * enregistrée. Le serveur déduit ce qu'il peut et laisse le stock tomber à 0/négatif.
   * Conserve les quantités d'origine (aucun ajustement). Marche pour conflit ET erreur.
   */
  async forceFullSale(saleId: string): Promise<boolean> {
    const sale = await offlineDb.pendingSales.get(saleId);
    if (!sale || sale.status === 'synced' || sale.status === 'syncing') {
      return false;
    }

    await offlineDb.pendingSales.update(saleId, {
      force: true,
      status: 'pending',
      errorMessage: undefined,
      conflictDetails: undefined,
      backroomDetails: undefined,
    });

    const updatedSale = await offlineDb.pendingSales.get(saleId);
    if (updatedSale && this.serverReachable) {
      return this.syncSingleSale(updatedSale);
    }
    return true;
  }
}

// ============================================================================
// FACTORY
// ============================================================================

/**
 * Créer une instance de SalesQueue pour un tenant
 */
export function createSalesQueue(tenantId: string): SalesQueue {
  return new SalesQueue(tenantId);
}
